import React, { useEffect, useState } from 'react';
import { Button } from '../../components/Button';
import { Checkbox } from '../../components/Checkbox';
import { Product } from '../../model/Products';
import { Subscription, SubscriptionId } from '../../model/Subscription';
import { Order } from '../../model/Order';
import { Row, Column } from 'react-foundation';
import styled from 'styled-components';
import { B2B2ChangeSubsRow } from './B2B2ChangeSubsRow';
import { useApiMutation, useApiQuery } from '../../utils/apiHooks';
import { ApiRequest } from '../../utils/api';
import { getAccountId } from '../../utils/helpers';
import _ from 'lodash';
import { Error } from '../../components/Error';

interface Props {
	currentPrice: number;
	currentOrder: Order;
	subscriptions: Subscription[];
	targetProducts: Product[];
	continueToCurrentOrder: () => void;
	onSubmit: (success: boolean) => void;
}

export const B2B2OfferingChange: React.FC<Props> = (props: Props) => {
	const { data: restrictions, error: restrictionsError } = useApiQuery(ApiRequest.getRestrictions(getAccountId()));
	const [selectedTargetProducts, setSelectedTargetProducts] = useState<Map<SubscriptionId, Product>>(new Map());
	const [termsAccepted, setTermsAccepted] = useState(false);
	const [password, setPassword] = useState('');
	const { data: customerData } = useApiQuery(ApiRequest.loadCustomerData(getAccountId()));
	const [offeringChange, { isSuccess: offeringChangeSuccess, error: offeringChangeError }] = useApiMutation((request: any) => ApiRequest.offeringChange(request));

	const subscriptionUnlimitedDataPrice: number = 14;
	const hasSubscriptions = props.subscriptions && props.subscriptions.length > 0;

	useEffect(() => {
		if (props.targetProducts !== undefined && props.subscriptions !== undefined) {
			const initialValue = new Map();
			props.subscriptions.forEach((subscription: Subscription) => {
				initialValue.set(subscription.subscriptionNumber, props.targetProducts[1]);
			});
			setSelectedTargetProducts(initialValue);
		}
	}, [props.targetProducts, props.subscriptions]);

	useEffect(() => {
		if (offeringChangeSuccess) {
			ApiRequest.logoutUnauthorized();
		} else if (offeringChangeError) {
			props.onSubmit(false);
			window.scrollTo(0, 0);
		}
	}, [offeringChangeSuccess, offeringChangeError]); // eslint-disable-line

	const subscriptionHasUnlimitedData = (subscription: Subscription) => {
		return subscription.settings?.unlimitedData?.value === true;
	};

	const getCurrentProductDescription = (subscription: Subscription) => {
		const order = props.currentOrder;
		return (
			<React.Fragment>
				Nyt: Dataa {order.dataPerSim === 'unlimited' || subscriptionHasUnlimitedData(subscription) ? 'rajattomasti kuukaudessa' : `${order.dataPerSim} Gt/kk`}, {order.moisPerSim} minuuttia tai
				tekstaria/kk {subscriptionHasUnlimitedData(subscription) ? '(liittymäkohtainen rajaton data valittuna)' : ''}
			</React.Fragment>
		);
	};

	const countCurrentPrice = () => {
		let sum = 0;
		props.subscriptions.forEach((subscription: Subscription) => {
			sum += subscriptionHasUnlimitedData(subscription) ? props.currentPrice + subscriptionUnlimitedDataPrice : props.currentPrice;
		});
		return sum;
	};

	const countSumBasedSelectedProductTypes = () => {
		let minSum = 0;
		let maxSum = 0;

		selectedTargetProducts.forEach((product: Product) => {
			minSum += product.eurPerMonth;
			maxSum += product.maxEurPerMonth ? product.maxEurPerMonth : product.eurPerMonth;
		});

		if (minSum === maxSum) {
			return minSum;
		} else {
			return `${minSum} - ${maxSum}`;
		}
	};

	const setSelectedProduct = (subscriptionNumber: string, product: Product) => {
		const clone = new Map(selectedTargetProducts);
		clone.set(subscriptionNumber, product);
		setSelectedTargetProducts(clone);
	};

	const handleSubmit = () => {
		const request = {
			subscriptions: Array.from(selectedTargetProducts.entries()).map((entry) => {
				const [subscriptionId, product] = entry;
				return {
					id: subscriptionId,
					packageType: product.productId
				};
			}),
			termsAccepted,
			password,
			newSubscriptionType: customerData?.upgradeableOffering
		};
		offeringChange(request);
	};

	const ableToOrder = restrictions !== undefined && _.findIndex(restrictions, (restriction) => restriction.id === 'OFFERING_CHANGE_BLOCKED') === -1;

	if (!restrictionsError && ableToOrder && hasSubscriptions) {
		return (
			<PageWrapper>
				<h2>Päivitä kaikkien liittymien sopimukset</h2>
				<h4>Valitse uusi korvaava Bisnes-liittymä aiemman liittymän tilalle.</h4>
				<p>
					Moi Bisnes Potti -liittymä on hyvä oletusvalinta kaikkiin liittymiin. Voit valita vaikkapa
					rajattoman tai 5G-liittymän heille, jotka käyttävät paljon nettiä tai kaipaavat vauhtia. Kaikkien
					liittymien päivittäminen onnistuu kerralla, parissa minuutissa. Kun päivität liittymäsi, uudet
					liittymät ovat käytössä välittömästi. Sinun ei tarvitse edes vaihtaa olemassa olevia Moin
					SIM-kortteja laitteisiisi, vaan nykyiset SIM-korttisi toimivat edelleen.
				</p>
				<div>
					<ProductsHeader>
						<Row>
							<Column className="columns small-6">Nykyinen liittymä</Column>
							<Column className="columns small-6 col-new">Uusi liittymä</Column>
						</Row>
					</ProductsHeader>
					<div>
						{props.subscriptions.map((subscription: Subscription, index: number) => (
							<div key={subscription.subscriptionNumber}>
								<B2B2ChangeSubsRow
									targetProducts={props.targetProducts}
									subscription={subscription}
									initialSelectedProduct={selectedTargetProducts.get(String(subscription.id))}
									currentPrice={subscriptionHasUnlimitedData(subscription) ? props.currentPrice + subscriptionUnlimitedDataPrice : props.currentPrice}
									productDesc={getCurrentProductDescription(subscription)}
									setSelectedProduct={(product: Product) => setSelectedProduct(subscription.subscriptionNumber, product)}
								/>
							</div>
						))}
					</div>
					<ProductsFooter>
						<Row>
							<Column className="columns small-12 medium-6 col-prev">
								Aiemmin yhteensä <MediumText>{countCurrentPrice()} €/kk</MediumText>
							</Column>
							<Column className="columns small-12 medium-6">
								Päivitetyt liittymät
								yhteensä <MediumText>{countSumBasedSelectedProductTypes()} €/kk</MediumText>
							</Column>
						</Row>
					</ProductsFooter>
				</div>
				<p className="disclaimer-text">
					Kunkin liittymän puhelut ja tekstarit Suomessa ja EU-maissa yhteensä enintään 4€/kk. Maksat
					puheluista ja tekstareista käytön mukaan 0,044 €/min tai kpl, et kuitenkaan koskaan enempää kuin
					4€/kk. Ei koske Bisnes Laitenettiä. <a href="https://www.moi.fi/yrityksille/hinnasto/" target="_blank" rel="noreferrer">Katso Bisnes
					Laitenetin hinnasto</a>.{' '}
					<a href="https://www.moi.fi/yrityksille/hinnasto/" target="_blank" rel="noreferrer">Katso koko hinnasto</a>.
				</p>
				<AgreementFieldWrapper>
					<Checkbox
						messageWidth={'80%'}
						checked={termsAccepted}
						message={
							<p>
								Luin ja hyväksyn Moin{' '}
								<a href="https://www.moi.fi/yrityksille/yleiset-sopimukset-ja-palvelukohtaiset-ehdot"
									  target="_blank" rel="noreferrer">
									sopimus- ja palveluehdot
								</a>
								.
							</p>
						}
						onChange={() => setTermsAccepted(!termsAccepted)}
					/>
				</AgreementFieldWrapper>

				<h4>Vahvista tilaus Moin salasanallasi</h4>
				<PwFieldWrapper>
					<label htmlFor="password">Salasana</label>
					<input type="password" id="password"
						   onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}/>
				</PwFieldWrapper>
				<p>
					Kun liittymien päivitys alkaa, sinut kirjataan ulos Yritys Moista ja pääset kirjautumaan sisään
					vasta kun päivitys on valmis. Liittymien päivitys kestää yleensä sekunneista muutamaan
					minuuttiin
				</p>
				<PwFieldWrapper>
					<Button disabled={!termsAccepted || password === ''} onClick={handleSubmit}>
						Vahvista tilaus
					</Button>
					<Button className="invisible" onClick={props.continueToCurrentOrder}>
						Ei sittenkään
					</Button>
				</PwFieldWrapper>

				<br/>
				<br/>

				<h3>Moin Bisnes-liittymissä on Euroopan reiluimmat puhe- ja tekstarihinnat</h3>
				<p>Euroopan reiluimmat liittymät* ovat saatavilla myös yrityksellesi. Kunkin liittymän puhelut ja
					tekstarit aina enintään 4 €/kk.**</p>
				<p>
					*Moin puhelut ja tekstarit kuuluvat Euroopan reiluimmin hinnoiteltujen mobiilipalvelujen joukkoon.
					Eläköön reiluus.
					<br/>
					**Ei koske Bisnes Laitenettiä. Katso{' '}
					<a href="https://www.moi.fi/yrityksille/hinnasto/" target="_blank" rel="noreferrer">
						Bisnes Laitenetin hinnasto
					</a>
					.
				</p>
			</PageWrapper>
		);
	} else if (!restrictionsError && ableToOrder) {
		return  (
			<PageWrapper>
				<h2>Päivitä sopimuksesi</h2>
				<h4 style={{ marginBottom: '1.5rem' }}>Hyväksy Moin sopimus ja palveluehdot ja vahvista päivitys salasanallasi.</h4>

				<AgreementFieldWrapper>
					<Checkbox
						messageWidth={'80%'}
						checked={termsAccepted}
						message={
							<p>
								Luin ja hyväksyn Moin{' '}
								<a href="https://www.moi.fi/yrityksille/yleiset-sopimukset-ja-palvelukohtaiset-ehdot" target="_blank" rel="noreferrer">
									sopimus- ja palveluehdot
								</a>
								.
							</p>
						}
						onChange={() => setTermsAccepted(!termsAccepted)}
					/>
				</AgreementFieldWrapper>

				<h4>Vahvista päivitys Moin salasanallasi</h4>
				<PwFieldWrapper>
					<label htmlFor="password">Salasana</label>
					<input type="password" id="password"
						   onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}/>
				</PwFieldWrapper>
				<p>
					Kun päivitys alkaa, sinut kirjataan ulos Yritys Moista ja pääset kirjautumaan sisään
					vasta kun päivitys on valmis. Päivitys kestää yleensä sekunneista muutamaan
					minuuttiin
				</p>
				<PwFieldWrapper>
					<Button disabled={!termsAccepted || password === ''} onClick={handleSubmit}>
						Vahvista päivitys
					</Button>
					<Button className="invisible" onClick={props.continueToCurrentOrder}>
						Ei sittenkään
					</Button>
				</PwFieldWrapper>

				<br/>
				<br/>

				<h3>Moin Bisnes-liittymissä on Euroopan reiluimmat puhe- ja tekstarihinnat</h3>
				<p>Euroopan reiluimmat liittymät* ovat saatavilla myös yrityksellesi. Kunkin liittymän puhelut ja
					tekstarit aina enintään 4 €/kk.**</p>
				<p>
					*Moin puhelut ja tekstarit kuuluvat Euroopan reiluimmin hinnoiteltujen mobiilipalvelujen joukkoon.
					Eläköön reiluus.
					<br/>
					**Ei koske Bisnes Laitenettiä. Katso{' '}
					<a href="https://www.moi.fi/yrityksille/hinnasto/" target="_blank" rel="noreferrer">
						Bisnes Laitenetin hinnasto
					</a>
					.
				</p>
			</PageWrapper>
		)
	} else if (!ableToOrder && restrictions !== undefined) {
		return (
			<RestrictionsMessage>
				<h4>Nyt ei kyllä mennyt kuin Strömsöössä…</h4>
				<p>Ups! Näyttää nyt siltä, että liittymälläsi/joillain liittymilläsi on irtisanominen tai aktivointi kesken</p>
				<p>Siksi emme voi päivittää liittymiäsi automaattisesti.</p>
				<p>Yritä uudelleen kun toimenpiteet on suoritettu loppuun.</p>
				<p>
					Jos haluat tarkan tiedon liittymien tilasta, kilauta vaikka <a href="#/account/customer-service">asiakaspalveluumme</a>
				</p>
				<p>
					<Button className="invisible" onClick={props.continueToCurrentOrder}>
						Jatka nykyisellä sopimuksella
					</Button>
				</p>
			</RestrictionsMessage>
		);
	} else if (restrictionsError !== null) {
		return <Error message={'Jotain meni vikaan. Yritä myöhemmin uudestaan tai ota yhteyttä asiakaspalveluumme'} />;
	}
	return <React.Fragment />;
};

const RestrictionsMessage = styled.div`
	text-align: center;
`;

const PageWrapper = styled.div`
	max-width: 900px;
	margin: 0 auto;

	p > a {
		color: #4c4c4c;
		text-decoration: underline;

		&:hover,
		&:focus {
			color: #ff0000;
		}
	}

	h2 {
		text-align: center;
		font-size: 1.818rem;
	}
	h3 {
		font-size: 1.5rem;
		line-height: 1.3;
	}
	h4,
	.h4 {
		font-size: 1.136rem;
	}

	p.disclaimer-text {
		margin: 2.5rem 0;
	}

	@media screen and (min-width: 800px) {
		h2 {
			font-size: 2.667rem;
		}
		h3 {
			font-size: 1.818rem;
		}
		h4,
		.h4 {
			font-size: 1.364rem;
		}
	}
`;

const AgreementFieldWrapper = styled.div`
	background: #fafafa;
	box-shadow: 0px 0px 15px rgba(0, 0, 0, 0.15);
	max-width: 500px;
	padding: 0.25rem 1rem;
	margin: 0 0 2rem;
`;

const PwFieldWrapper = styled.div`
	max-width: 15rem;

	button {
		width: 100%;
		max-width: none;
	}
`;

const MediumText = styled.span`
	font-weight: 500;
`;

const ProductsHeader = styled.div`
	display: none;

	@media screen and (min-width: 800px) {
		display: block;
		margin-bottom: 0.75rem;

		.col-new {
			padding-left: 36px;
		}
	}
`;

const ProductsFooter = styled.div`
	font-weight: bold;
	margin-top: 1rem;

	.col-prev {
		text-decoration: line-through;
	}

	.medium-6 {
		width: 100%;
	}

	@media screen and (min-width: 800px) {
		text-align: right;

		.medium-6 {
			width: 50%;
		}
		.columns {
			padding-right: 1.5rem;

			&.col-prev {
				padding-right: 0;
			}
		}
	}
`;
